import { useState } from 'react';
import { Explain } from '@/components/Explain';
import { cn } from '@/lib/cn';
import { formatTime, type TimeZoneMode } from '@/lib/format';
import type { PlayerActivityDay, PlayerActivityHour } from '@/types';

/**
 * Two magnitude-over-time views, drawn as inline SVG — no chart library, because
 * the console iframe makes ZERO external requests.
 *
 * Both are a single series (bet count), so per the dataviz method they use ONE
 * sequential hue (the console accent), light→dark by magnitude. The signal
 * colours (crit/warn) are reserved for the Risk panel, so they still mean
 * "something is wrong" and nothing else. Peak bar is direct-labelled; every bar
 * has a hover tooltip.
 *
 * ⚠️ Hours and days are UTC (see the datasource) — a fraud pattern like
 * "only bets at 03:00" must mean one fixed clock, not the viewer's.
 */
export function ActivityCharts({
  perDay,
  perHour,
  tz,
  unit,
}: {
  perDay: PlayerActivityDay[];
  perHour: PlayerActivityHour[];
  tz: TimeZoneMode;
  unit: string;
}) {
  return (
    <section className="grid gap-px border-b border-hairline bg-hairline lg:grid-cols-2">
      <div className="bg-surface p-4">
        <div className="eyebrow mb-3">
          <Explain term="activity_hours">bets by hour — UTC</Explain>
        </div>
        <HourHistogram perHour={perHour} />
      </div>
      <div className="bg-surface p-4">
        <div className="eyebrow mb-3">bets by day — UTC</div>
        <DayBars perDay={perDay} tz={tz} unit={unit} />
      </div>
    </section>
  );
}

/**
 * ⚠️ `bets` arrives as a STRING from ClickHouse's JSON (toUInt64 → string), so
 * every comparison and max MUST go through Number() — a raw `>` would compare
 * lexicographically ("555" < "60") and pick the wrong peak while the bars, which
 * Math.max coerces, still looked right.
 */
const n = (v: number | string): number => Number(v) || 0;

/** 24 fixed slots, so an empty hour reads as a gap rather than being dropped. */
function HourHistogram({ perHour }: { perHour: PlayerActivityHour[] }) {
  const byHour = new Map(perHour.map((h) => [h.hour, h]));
  const slots = Array.from({ length: 24 }, (_, h) => byHour.get(h) ?? { hour: h, bets: 0, bet: '0' });
  const max = Math.max(1, ...slots.map((s) => n(s.bets)));
  const peak = slots.reduce((a, b) => (n(b.bets) > n(a.bets) ? b : a), slots[0]);

  const [hover, setHover] = useState<number | null>(null);

  return (
    <div>
      <div className="flex h-[120px] items-end gap-[3px]">
        {slots.map((s) => (
          <Bar
            key={s.hour}
            frac={n(s.bets) / max}
            active={hover === s.hour}
            emphasise={s.hour === peak.hour && n(peak.bets) > 0}
            onEnter={() => setHover(s.hour)}
            onLeave={() => setHover(null)}
            tip={`${String(s.hour).padStart(2, '0')}:00 · ${s.bets} bets`}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between font-mono text-[9px] text-text-faint">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>23</span>
      </div>
      {peak.bets > 0 && (
        <p className="mt-1.5 text-[11px] text-text-dim">
          Busiest hour <span className="font-mono text-text">{String(peak.hour).padStart(2, '0')}:00</span>{' '}
          UTC · <span className="tnum font-mono">{peak.bets}</span> bets
        </p>
      )}
    </div>
  );
}

function DayBars({
  perDay,
  tz,
  unit,
}: {
  perDay: PlayerActivityDay[];
  tz: TimeZoneMode;
  unit: string;
}) {
  const max = Math.max(1, ...perDay.map((d) => n(d.bets)));
  const peak = perDay.reduce<PlayerActivityDay | null>((a, b) => (!a || n(b.bets) > n(a.bets) ? b : a), null);
  const [hover, setHover] = useState<number | null>(null);

  if (perDay.length === 0) {
    return <p className="py-8 text-center text-[12px] text-text-dim">No activity in this window.</p>;
  }

  return (
    <div>
      <div className="flex h-[120px] items-end gap-[2px]">
        {perDay.map((d, i) => (
          <Bar
            key={d.day}
            frac={n(d.bets) / max}
            active={hover === i}
            emphasise={d.day === peak?.day && n(peak?.bets ?? 0) > 0}
            onEnter={() => setHover(i)}
            onLeave={() => setHover(null)}
            tip={`${d.day} · ${d.bets} bets · ${fmt(d.bet)} ${unit} staked`}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between font-mono text-[9px] text-text-faint">
        <span>{perDay[0]?.day.slice(5)}</span>
        <span>{perDay[perDay.length - 1]?.day.slice(5)}</span>
      </div>
      {peak && (
        <p className="mt-1.5 text-[11px] text-text-dim">
          Busiest day{' '}
          <span className="font-mono text-text">
            {formatTime(new Date(`${peak.day}T00:00:00Z`), tz, true).slice(0, 10)}
          </span>{' '}
          · <span className="tnum font-mono">{peak.bets}</span> bets
        </p>
      )}
    </div>
  );
}

/**
 * One bar. Magnitude is encoded by HEIGHT; a subtle lightness lift on taller
 * bars (single-hue sequential) reinforces it without introducing a second hue.
 * 2px rounded top per the mark spec. Hover shows the tooltip; the whole cell is
 * the hit target, not just the visible fill.
 */
function Bar({
  frac,
  active,
  emphasise,
  onEnter,
  onLeave,
  tip,
}: {
  frac: number;
  active: boolean;
  emphasise: boolean;
  onEnter: () => void;
  onLeave: () => void;
  tip: string;
}) {
  return (
    <div
      className="group relative flex h-full flex-1 cursor-default items-end"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div
        className={cn(
          'w-full rounded-t-[2px] transition-colors',
          emphasise ? 'bg-accent' : active ? 'bg-accent/80' : 'bg-accent/45',
        )}
        style={{ height: `${Math.max(frac * 100, frac > 0 ? 3 : 0)}%` }}
      />
      {active && (
        <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 whitespace-nowrap border border-hairline-strong bg-surface-raised px-2 py-1 font-mono text-[10.5px] text-text shadow-sm">
          {tip}
        </div>
      )}
    </div>
  );
}

function fmt(value: string): string {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 0 }) : value;
}
